import { z } from 'zod';

// src/domain/types.ts
var POST_STATUSES = [
  "draft",
  "scheduled",
  "processing",
  "published",
  "partial",
  "failed",
  "cancelled"
];
var TARGET_STATUSES = [
  "pending",
  "processing",
  "published",
  "failed",
  "cancelled"
];
var ACCOUNT_STATUSES = ["active", "expired", "revoked", "error"];
var PROVIDERS = ["x", "mock"];
function toPublicAccount(account) {
  const { accessToken: _accessToken, refreshToken: _refreshToken, ...pub } = account;
  return pub;
}

// src/domain/constants.ts
var X_MAX_TWEET_LENGTH = 280;
var X_MAX_MEDIA_PER_POST = 4;
var X_MAX_IMAGE_BYTES = 5 * 1024 * 1024;
var X_ALLOWED_MEDIA_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif"
];
var X_MAX_ALT_TEXT_LENGTH = 1e3;
var MAX_ACCOUNTS_PER_POST = 25;
var MIN_SCHEDULE_LEAD_SECONDS = 60;
var T_CO_LENGTH = 23;
function countTweetLength(text) {
  const urlPattern = /https?:\/\/[^\s]+/g;
  const urls = text.match(urlPattern) ?? [];
  const withoutUrls = text.replace(urlPattern, "");
  let weight = urls.length * T_CO_LENGTH;
  for (const char of withoutUrls) {
    const cp = char.codePointAt(0) ?? 0;
    weight += isDoubleWidth(cp) ? 2 : 1;
  }
  return weight;
}
function isDoubleWidth(cp) {
  return cp >= 4352 && cp <= 4447 || // Hangul Jamo
  cp >= 11904 && cp <= 12350 || // CJK radicals, punctuation
  cp >= 12353 && cp <= 13311 || // Hiragana, Katakana, CJK compat
  cp >= 13312 && cp <= 19903 || // CJK Ext A
  cp >= 19968 && cp <= 40959 || // CJK Unified
  cp >= 40960 && cp <= 42191 || // Yi
  cp >= 44032 && cp <= 55203 || // Hangul Syllables
  cp >= 63744 && cp <= 64255 || // CJK Compat Ideographs
  cp >= 65072 && cp <= 65135 || // CJK Compat Forms
  cp >= 65280 && cp <= 65376 || // Fullwidth Forms
  cp >= 65504 && cp <= 65510 || cp >= 126976 && cp <= 131071 || // Emoji & symbols
  cp >= 131072 && cp <= 262141;
}
function isWithinTweetLimit(text) {
  return countTweetLength(text) <= X_MAX_TWEET_LENGTH;
}
var mediaAttachmentSchema = z.object({
  path: z.string().min(1),
  url: z.string().url(),
  mimeType: z.enum(X_ALLOWED_MEDIA_TYPES),
  sizeBytes: z.number().int().positive().max(X_MAX_IMAGE_BYTES),
  altText: z.string().max(X_MAX_ALT_TEXT_LENGTH).optional()
});
var tweetContentSchema = z.string().trim().min(1, "Write something before posting.").refine(
  (text) => countTweetLength(text) <= X_MAX_TWEET_LENGTH,
  (text) => ({
    message: `Post is ${countTweetLength(text)} characters \u2014 ${X_MAX_TWEET_LENGTH} is the limit. Links count as 23, emoji as 2.`
  })
);
var accountIdsSchema = z.array(z.string().uuid()).min(1, "Select at least one account.").max(MAX_ACCOUNTS_PER_POST, `You can post to at most ${MAX_ACCOUNTS_PER_POST} accounts at once.`).refine((ids) => new Set(ids).size === ids.length, "Duplicate account selected.");
var futureDateSchema = z.string().datetime({ offset: true }).refine(
  (iso) => new Date(iso).getTime() > Date.now() + MIN_SCHEDULE_LEAD_SECONDS * 1e3,
  `Pick a time at least ${MIN_SCHEDULE_LEAD_SECONDS / 60} minute(s) from now.`
);
var createPostSchema = z.discriminatedUnion("intent", [
  z.object({
    intent: z.literal("now"),
    content: tweetContentSchema,
    accountIds: accountIdsSchema,
    media: z.array(mediaAttachmentSchema).max(X_MAX_MEDIA_PER_POST).default([])
  }),
  z.object({
    intent: z.literal("schedule"),
    content: tweetContentSchema,
    accountIds: accountIdsSchema,
    media: z.array(mediaAttachmentSchema).max(X_MAX_MEDIA_PER_POST).default([]),
    scheduledAt: futureDateSchema
  }),
  z.object({
    intent: z.literal("draft"),
    // A draft is allowed to be empty and account-less — that's the point of a draft.
    content: z.string().max(4e3).default(""),
    accountIds: z.array(z.string().uuid()).max(MAX_ACCOUNTS_PER_POST).default([]),
    media: z.array(mediaAttachmentSchema).max(X_MAX_MEDIA_PER_POST).default([]),
    scheduledAt: z.string().datetime({ offset: true }).nullable().default(null)
  })
]);
var updatePostSchema = z.object({
  content: tweetContentSchema.optional(),
  accountIds: accountIdsSchema.optional(),
  media: z.array(mediaAttachmentSchema).max(X_MAX_MEDIA_PER_POST).optional(),
  scheduledAt: futureDateSchema.nullable().optional()
}).refine((data) => Object.keys(data).length > 0, "Nothing to update.");
var queueFiltersSchema = z.object({
  status: z.array(z.enum(POST_STATUSES)).optional(),
  accountIds: z.array(z.string().uuid()).optional(),
  search: z.string().trim().max(200).optional(),
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  sortBy: z.enum(["scheduledAt", "createdAt", "status"]).default("scheduledAt"),
  sortDir: z.enum(["asc", "desc"]).default("asc")
});
var oauthStartSchema = z.object({
  provider: z.enum(PROVIDERS).default("x"),
  /** Where to bounce the user back to in the SPA once the callback completes. */
  returnTo: z.string().startsWith("/").max(500).default("/accounts")
});
var oauthCallbackSchema = z.object({
  code: z.string().min(1).optional(),
  state: z.string().min(1),
  error: z.string().optional(),
  error_description: z.string().optional()
});
var targetStatusSchema = z.enum(TARGET_STATUSES);
var postStatusSchema = z.enum(POST_STATUSES);
var retryPostSchema = z.object({
  accountIds: z.array(z.string().uuid()).optional()
});

// src/errors.ts
var AppError = class _AppError extends Error {
  code;
  status;
  retryable;
  retryAfterSeconds;
  details;
  constructor(options) {
    super(options.message, { cause: options.cause });
    this.name = "AppError";
    this.code = options.code;
    this.status = options.status ?? defaultStatusFor(options.code);
    this.retryable = options.retryable ?? defaultRetryableFor(options.code);
    this.retryAfterSeconds = options.retryAfterSeconds;
    this.details = options.details;
  }
  /** Safe to send to the browser: no `cause`, no stack, no token material. */
  toJSON() {
    return {
      error: {
        code: this.code,
        message: this.message,
        ...this.retryAfterSeconds !== void 0 && { retryAfterSeconds: this.retryAfterSeconds },
        ...this.details && { details: this.details }
      }
    };
  }
  static isAppError(error) {
    return error instanceof _AppError;
  }
};
function defaultStatusFor(code) {
  switch (code) {
    case "validation_error":
    case "content_too_long":
    case "media_invalid":
    case "duplicate_content":
      return 400;
    case "unauthorized":
    case "token_expired":
    case "token_revoked":
    case "oauth_failed":
      return 401;
    case "forbidden":
      return 403;
    case "not_found":
      return 404;
    case "rate_limited":
      return 429;
    case "provider_unavailable":
    case "network_error":
      return 503;
    case "internal_error":
      return 500;
  }
}
function defaultRetryableFor(code) {
  switch (code) {
    case "rate_limited":
    case "provider_unavailable":
    case "network_error":
      return true;
    default:
      return false;
  }
}
var errors = {
  validation: (message, details) => new AppError({ code: "validation_error", message, details }),
  notFound: (resource) => new AppError({ code: "not_found", message: `${resource} not found` }),
  unauthorized: (message = "Authentication required") => new AppError({ code: "unauthorized", message }),
  forbidden: (message = "You do not have access to this resource") => new AppError({ code: "forbidden", message }),
  /**
   * X rejects a tweet whose text exactly matches a recent one from the same
   * account. This is the single most common bulk-posting failure, so it gets
   * its own code and a message the UI can show verbatim.
   */
  duplicateContent: () => new AppError({
    code: "duplicate_content",
    message: "X rejected this post as a duplicate. X blocks identical text posted repeatedly from the same account \u2014 vary the wording and try again."
  }),
  contentTooLong: (length, max) => new AppError({
    code: "content_too_long",
    message: `Post is ${length} characters; the limit is ${max}.`,
    details: { length, max }
  }),
  tokenExpired: (accountId) => new AppError({
    code: "token_expired",
    message: "This account\u2019s access has expired. Reconnect it to keep posting.",
    details: { accountId }
  }),
  tokenRevoked: (accountId) => new AppError({
    code: "token_revoked",
    message: "Access to this account was revoked on X. Reconnect it to keep posting.",
    details: { accountId }
  }),
  rateLimited: (retryAfterSeconds, details) => new AppError({
    code: "rate_limited",
    message: `Rate limited by X. Retrying in ${retryAfterSeconds}s.`,
    retryAfterSeconds,
    details
  }),
  providerUnavailable: (message = "X is temporarily unavailable.") => new AppError({ code: "provider_unavailable", message }),
  network: (cause) => new AppError({
    code: "network_error",
    message: "Could not reach X. This will be retried automatically.",
    cause
  }),
  internal: (message = "Something went wrong.", cause) => new AppError({ code: "internal_error", message, cause })
};

export { ACCOUNT_STATUSES, AppError, MAX_ACCOUNTS_PER_POST, MIN_SCHEDULE_LEAD_SECONDS, POST_STATUSES, PROVIDERS, TARGET_STATUSES, T_CO_LENGTH, X_ALLOWED_MEDIA_TYPES, X_MAX_ALT_TEXT_LENGTH, X_MAX_IMAGE_BYTES, X_MAX_MEDIA_PER_POST, X_MAX_TWEET_LENGTH, countTweetLength, createPostSchema, errors, isWithinTweetLimit, mediaAttachmentSchema, oauthCallbackSchema, oauthStartSchema, postStatusSchema, queueFiltersSchema, retryPostSchema, targetStatusSchema, toPublicAccount, tweetContentSchema, updatePostSchema };
//# sourceMappingURL=chunk-EJQTQ2QR.js.map
//# sourceMappingURL=chunk-EJQTQ2QR.js.map